import axios from 'axios';
import { API_URL } from '../../config';
import type { AuthTokens } from '../../models/auth';
import { fetchXimiStimulinIdentity, XimiStimulinIdentityClaims } from './ximiStimulinIdentity';
import { setAccessToken, setRefreshToken } from '../../utilities/storage/tokenStorage';
import { ximiUserManager } from '../../utilities/ximi/oidcConfig';
import { getCurrentXimiSsoAccessToken } from '../../utilities/ximi/ximiSsoSession';
import { deriveXimiAccountFromSsoProfile } from '../../utilities/ximi/ximiSsoProfile';
import { upsertXimiAccount } from '../../utilities/ximi/storage/ximiAccounts';

/**
 * Enrichit le compte courant avec les infos du JWT d'identité (alignement Ximi Famille/
 * Mobile : `NOM, Prénom` + structure). Cosmétique : ne doit jamais faire échouer le sign-in.
 */
async function enrichXimiAccountFromIdentity(claims: XimiStimulinIdentityClaims): Promise<void> {
    try {
        const user = await ximiUserManager.getUser();
        if (!user) return;
        const account = deriveXimiAccountFromSsoProfile(user.profile);
        if (!account) return;

        const displayName = [claims.lastName, claims.firstName].filter(Boolean).join(', ');
        await upsertXimiAccount({
            ...account,
            ...(displayName ? { displayName } : {}),
            ...(claims.agencyName ? { structureName: claims.agencyName } : {})
        });
    } catch {
        // enrichissement d'affichage uniquement — silencieux
    }
}

/** Échec de connexion Ximi, avec le code métier quand Ozmo en fournit un. */
export type XimiSignInResult = { ok: true; tokens: AuthTokens } | { ok: false; code?: string };

/**
 * Récupère l'identité Ximi (JWT signé par Ozmo), l'échange sur /v2/auth/ximi contre les
 * tokens Stimul'In et les stocke. En cas d'échec, remonte le code métier s'il existe : c'est
 * lui qui permet d'expliquer le refus plutôt que d'afficher une erreur générique.
 */
export async function signInWithXimiIdentity(): Promise<XimiSignInResult> {
    try {
        const identity = await fetchXimiStimulinIdentity();
        if (!identity.ok) return { ok: false, code: identity.code };

        const ssoToken = await getCurrentXimiSsoAccessToken();
        if (!ssoToken) return { ok: false }; // session SSO absente/expirée → sign-in impossible

        const { data } = await axios.post<AuthTokens>(`${API_URL}/auth/ximi`, {
            token: identity.token,
            ssoToken
        });
        await setAccessToken(data.accessToken);
        await setRefreshToken(data.refreshToken);
        await enrichXimiAccountFromIdentity(identity.claims);
        return { ok: true, tokens: data };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[XimiSignIn] échec échange /v2/auth/ximi', error);
        return { ok: false };
    }
}
